import { z } from 'zod';
export const maxUploadFileSize = 20 * 1024 * 1024; // 20 MB
// Reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
export const allowUploadMimeTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/vnd.microsoft.icon',
    'image/x-icon',
    'image/svg+xml',
    'image/tiff',
    'image/webp',
    'image/bmp',
    'application/zip',
];
const allowUploadMimeTypeGuard = z.enum(allowUploadMimeTypes);
export const userAssetsServiceStatusGuard = z.object({
    status: z.union([z.literal('ready'), z.literal('not_configured')]),
    allowUploadMimeTypes: z.array(allowUploadMimeTypeGuard).optional(),
    maxUploadFileSize: z.number().optional(),
    /** Whether Experience avatar upload during sign-up is available on this server. */
    isExperienceAvatarUploadEnabled: z.boolean().optional(),
});
export const userAssetsGuard = z.object({
    url: z.string(),
});
export const uploadFileGuard = z.object({
    filepath: z.string(),
    mimetype: z.string(),
    originalFilename: z.string(),
    size: z.number(),
});
/**
 * Build a guard for a multipart file field, normalizing the value to an array of files.
 *
 * `koa-body` v8 unwraps a single uploaded file to a bare object (only multi-file fields stay as
 * arrays), so a plain `uploadFileGuard.array()` rejects single-file uploads. This wraps the value
 * in an array first, then applies the given array guard so routes can keep treating the field as a
 * list. Pass the `.min()`/`.max()`-chained `uploadFileGuard.array()` as `arrayGuard`.
 */
export const buildUploadFilesGuard = (arrayGuard) => z.preprocess((value) => (Array.isArray(value) ? value : [value]), arrayGuard);
export const mimeTypeToFileExtensionMappings = Object.freeze({
    'image/jpeg': ['jpeg', 'jpg'],
    'image/png': ['png'],
    'image/gif': ['gif'],
    'image/vnd.microsoft.icon': ['ico'],
    'image/x-icon': ['ico'],
    'image/svg+xml': ['svg'],
    'image/tiff': ['tif', 'tiff'],
    'image/webp': ['webp'],
    'image/bmp': ['bmp'],
    'application/zip': ['zip'],
});
/** MIME types allowed for avatar uploads (Experience, Account Center, Console). */
export const avatarMimeTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
];
const avatarMimeTypeSet = new Set(avatarMimeTypes);
export const isAvatarMimeType = (mimeType) => avatarMimeTypeSet.has(mimeType);
export const avatarFileAccept = avatarMimeTypes.join(',');
const formatAvatarExtensionLabel = (extension) => extension === 'webp' ? 'WebP' : extension.toUpperCase();
export const avatarFileExtensionsLabel = avatarMimeTypes
    .map((mimeType) => formatAvatarExtensionLabel(mimeTypeToFileExtensionMappings[mimeType][0]))
    .join(', ');
